import React, { Suspense, useDeferredValue, useEffect, useState } from 'react';
import {
  Box,
  Combobox,
  FormControl,
  TextFieldSize,
  Text,
  ToastAction,
  ToastType,
  useToastsDispatch,
  TextFieldAppearance,
  AvatarSize,
  theme,
  SegmentedControl,
  SegmentedControlOption,
  SegmentedControlSize,
} from '@bechtel/gds';
import {
  ErrorBoundary,
  SuspenseLoader,
  useName,
  useDebounce,
  ProjectAppTypes,
  useEnvProvider,
} from '@bechtel/client-common';
import { ADD_USER, GET_LOB_EMPLOYEES } from '@bechtel/client-gql';
import {
  User,
  AddUserInput,
  AddUserPayload,
  AddUserError,
  LobEmployee,
  LobEmployeeList,
  PagingInput,
} from '@bechtel/types';
import { get, filter, map } from 'lodash';
import { ApolloError, NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import { AvatarRegularStyled } from '../styled';
import {
  HeaderStyled,
  HeaderUserDetailStyled,
  HeadingStyled,
  UserDetailContainerStyled,
  TextFieldStyled,
} from './styled';
import { UserInfoDetail } from '../detail/detail';
import { UserAddError } from './error';
import { useNavigate } from 'react-router-dom';
import { t, Trans } from '@lingui/macro';
import { FormikState } from '~/user-admin/users/form-state';
import { Formik, FormikProps, Field } from 'formik';
import { object, array, string } from 'yup';

const FormControlLabel = ({ label, disabled }) => {
  return (
    <Box css={disabled ? 'opacity:0.5' : ''}>
      <Text family="graphik" color="bechtel.blue.800" size="300">
        {label}
      </Text>
    </Box>
  );
};

const ItemComponent = ({ item }: any) => {
  const { getFullName } = useName();
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.SMALL}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box flexDirection={{ sm: 'column' }}>
        <HeadingStyled>{getFullName(item.firstName, item.lastName)}</HeadingStyled>
      </Box>
    </Box>
  );
};
const SelectedItemComponent = ({ item }: any) => {
  const { getFullName } = useName();
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.crewMember && item.crewMember.imageUrl ? true : false}
          size={AvatarSize.SMALL}
          src={item && item.crewMember && item.crewMember.imageUrl ? item.crewMember.imageUrl : undefined}
        />
      </Box>
      <Box flexDirection={{ sm: 'column' }}>
        <HeadingStyled>{getFullName(item.firstName, item.lastName)}</HeadingStyled>
      </Box>
    </Box>
  );
};

const initialValues: FormikState = {
  roles: [],
  disciplines: [],
  company: null,
  facilities: [],
  isActive: false,
  jobTitle: '',
  becimsId: '',
  firstName: '',
  lastName: '',
  lobEmployeeId: '',
  bun: '',
};

export const CreateUser = () => {
  const { envConfig } = useEnvProvider();
  const navigate = useNavigate();
  const { getFullName } = useName();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDeferredValue(searchText);
  const itemToString = (item) => getFullName(item.firstName, item.lastName);
  const [selectedLobEmployee, setSelectedLobEmployee] = useState<User | null>(null);
  const [addUserError, setAddUserError] = useState<AddUserError | ApolloError | null>(null);
  const dispatchToast = useToastsDispatch();
  const [isProjectUser, setIsProjectUser] = useState<boolean>(true);
  const [formikInitialValues, setFormikInitialValues] = useState<FormikState>(initialValues);
  const isPFAppType = envConfig.appName === ProjectAppTypes.PF;
  let form;

  const userFulllName = selectedLobEmployee
    ? getFullName(selectedLobEmployee.firstName, selectedLobEmployee.lastName)
    : '';

  const UserUpdateToast = () => {
    return (
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          {userFulllName}&nbsp;{t`successfully activated.`}
        </Text>
      </Box>
    );
  };

  const createSuccessToast: ToastAction = {
    type: 'ADD_TOAST',
    payload: {
      toastType: ToastType.INFO,
      content: UserUpdateToast,
      autoDismissTimeout: 5000,
    },
  };

  const [getLobEmployeeList, { data: empData, loading: empLoading, networkStatus: empNetworkStatus }] = useLazyQuery<
    { lobEmployees: LobEmployeeList },
    { searchKeyword: string; paging: PagingInput }
  >(GET_LOB_EMPLOYEES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const lobEmployeeList = get(empData, 'lobEmployees.items', []) as LobEmployeeList['items'];
  const filteredLobEmployeeList = filter(lobEmployeeList, (employee) => !employee.isBPSUser);

  useEffect(() => {
    if (debouncedSearchText?.length < 3) return;
    getLobEmployeeList({
      variables: {
        searchKeyword: debouncedSearchText,
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [debouncedSearchText, getLobEmployeeList]);
  const onLobEmployeeSelect = (lobEmployee: LobEmployee) => {
    const user: User = {
      id: lobEmployee?.id ? lobEmployee.id : '',
      firstName: lobEmployee.firstName.trim(),
      lastName: lobEmployee.lastName.trim(),
      etrackId: lobEmployee.id,
      bun: lobEmployee.bun?.trim(),
      email: lobEmployee.email,
      phoneNumber: lobEmployee.phone,
      mobileCarrier: lobEmployee.carrier,
      crewMember: {
        id: '',
        firstName: lobEmployee.firstName,
        lastName: lobEmployee.lastName,
        imageUrl: lobEmployee.imageUrl,
      },
      supervisor: {
        id: lobEmployee.supervisor?.id ? lobEmployee.supervisor?.id : '',
        firstName: lobEmployee.supervisor?.firstName ? lobEmployee.supervisor?.firstName : '',
        lastName: lobEmployee.supervisor?.lastName ? lobEmployee.supervisor?.lastName : '',
        bun: lobEmployee.supervisor?.bun?.trim(),
        etrackId: lobEmployee.supervisor?.id,
        permissionScopes: [],
        permissions: [],
        roles: [],
        crewMember: {
          id: '',
          firstName: lobEmployee.supervisor?.firstName ? lobEmployee.supervisor?.firstName : '',
          lastName: lobEmployee.supervisor?.lastName ? lobEmployee.supervisor?.lastName : '',
          imageUrl: lobEmployee.supervisor?.imageUrl ? lobEmployee.supervisor?.imageUrl : '',
        },
      },
      permissionScopes: [],
      permissions: [],
      roles: [],
    };
    setSelectedLobEmployee(user);
    const innerInitialValues = {
      ...initialValues,
      isActive: true,
      roles: [],
      disciplines: [],
      facilities: [],
      jobTitle: '',
      becimsId: '',
      company: null,
      firstName: lobEmployee?.firstName ? lobEmployee.firstName : null,
      lastName: lobEmployee?.lastName ? lobEmployee.lastName : null,
      lobEmployeeId: lobEmployee?.id ? lobEmployee.id : '',
      bun: lobEmployee?.bun ? lobEmployee.bun.trim() : '',
    };
    //TODO: Formik rendering with old initialValues. Analyse the formik to remove the state.
    setFormikInitialValues(innerInitialValues);
  };

  const [addUser, { loading: mutationLoading }] = useMutation<{ addUser: AddUserPayload }, { input: AddUserInput }>(
    ADD_USER,
    {
      onCompleted: (data) => {
        const updatedUserError = get(data, 'addUser.error');
        if (updatedUserError) {
          setAddUserError(updatedUserError);
        } else {
          setAddUserError(null);
          dispatchToast(createSuccessToast);
          navigate({
            pathname: `/project-admin/user-admin/${data.addUser.user?.id}`,
            search: `?refreshList=true`,
          });
        }
      },
      onError: (error) => {
        setAddUserError(error);
      },
      refetchQueries: ['users'],
    }
  );

  useEffect(() => {
    if (!isProjectUser) setFormikInitialValues(initialValues);
  }, [isProjectUser]);

  const addUserValidationSchema = object().shape({
    roles: array()
      .required()
      .min(1, t`Select at least 1 ${isPFAppType ? 'PipeFighter' : 'ArcStryker'} Role`),
    bun: string().required(t`BUN is mandatory`),
    firstName: string().required(t`BUN is mandatory`),
    lastName: string().required(t`BUN is mandatory`),
  });

  return (
    <Formik
      enableReinitialize={true}
      initialValues={formikInitialValues}
      validationSchema={addUserValidationSchema}
      onSubmit={async (values: FormikState) => {
        const input: AddUserInput = {
          lobEmployeeId: values.lobEmployeeId,
          roleIds: map(values.roles, 'id'),
          companyId: values.company?.id,
          workPackageDisciplineIds: map(values.disciplines, 'id'),
          facilityIds: map(values.facilities, 'id'),
          becImsId: values.becimsId?.trim(),
          firstName: values.firstName?.trim(),
          lastName: values.lastName?.trim(),
          bun: values?.bun ? values.bun.trim() : '',
          jobTitle: values.jobTitle?.trim(),
        };
        addUser({
          variables: { input },
        });
      }}
    >
      {(formikProps: FormikProps<FormikState>) => {
        form = formikProps;
        return (
          <UserDetailContainerStyled>
            <HeaderStyled>
              <Text size="500" color="grey.1000" weight="600" family="graphik">
                {isProjectUser
                  ? t`Activate New ${isPFAppType ? 'PipeFighter' : 'ArcStryker'} User`
                  : t`Activate New User`}
              </Text>
              <SegmentedControl>
                <SegmentedControlOption
                  selected={isProjectUser}
                  onClick={() => setIsProjectUser(true)}
                  segmentSize={SegmentedControlSize.SMALL}
                  css={{ 'font-weight': '400 !important' }}
                >
                  <Trans>Project Users</Trans>
                </SegmentedControlOption>
                <SegmentedControlOption
                  selected={!isProjectUser}
                  onClick={() => setIsProjectUser(false)}
                  segmentSize={SegmentedControlSize.SMALL}
                  css={{ 'font-weight': '400 !important' }}
                >
                  <Trans>Non-Project Users</Trans>
                </SegmentedControlOption>
              </SegmentedControl>
            </HeaderStyled>
            <Box p={{ sm: '600' }} pb={{ sm: '100' }} flexDirection={{ sm: 'column' }}>
              <Text size="300" color="grey.600" weight="600" family="graphik">
                {isProjectUser
                  ? t`Search for the person you’d like to activate within ${isPFAppType ? 'PipeFighter' : 'ArcStryker'}`
                  : t`Add a new user you’d like to activate within ${isPFAppType ? 'PipeFighter' : 'ArcStryker'}`}
              </Text>
            </Box>
            <Box p={{ sm: '600' }} pb={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
              {isProjectUser ? (
                <FormControl
                  label={<FormControlLabel disabled={false} label={t`Search by name or unique ID`} />}
                  css={{ marginBottom: theme.spacings[100] }}
                  data-testid="type-crewmember"
                >
                  <Combobox
                    ariaLabel={t`Search by name or unique ID`}
                    disabled={false}
                    size={TextFieldSize.LARGE}
                    items={filteredLobEmployeeList}
                    appearance={TextFieldAppearance.NORMAL}
                    itemString={itemToString}
                    itemComponent={ItemComponent}
                    selectedComponent={SelectedItemComponent}
                    selectedItem={selectedLobEmployee}
                    onSelect={(selectedItem: any) => {
                      onLobEmployeeSelect(selectedItem);
                    }}
                    term={searchText}
                    setTerm={setSearchText}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchText(event.target.value);
                    }}
                    restorePreviousOnSkip
                    loading={empLoading || empNetworkStatus === NetworkStatus.refetch}
                    messageOnNoResults={t`No users found`}
                  ></Combobox>
                </FormControl>
              ) : (
                <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={t`Bun`} disabled={false} />}>
                  <Field name="bun">
                    {({ field }) => {
                      return <TextFieldStyled disabled={false} {...field} />;
                    }}
                  </Field>
                </FormControl>
              )}
            </Box>
            <HeaderUserDetailStyled>
              <Text size="500" color="grey.1000" weight="600" family="graphik">
                <Trans>Review User Details</Trans>
              </Text>
            </HeaderUserDetailStyled>
            <UserInfoDetail
              userDetail={selectedLobEmployee}
              mutationLoading={mutationLoading}
              formikProps={formikProps}
              isProjectUser={isProjectUser}
              isNewUser={true}
            />
            {addUserError && (
              <ErrorBoundary>
                <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                  <UserAddError
                    addUserError={addUserError}
                    close={() => {
                      setAddUserError(null);
                    }}
                  />
                </Suspense>
              </ErrorBoundary>
            )}
          </UserDetailContainerStyled>
        );
      }}
    </Formik>
  );
};
